import { PagingParameters } from "@/api/dtos";
import { readTablePreferences } from "@/hooks/useTablePreferences";
import {
  useCountRelatedFields,
  useCreateRelatedFields,
  useDeleteRelation,
  useGetNotRelatedResource,
  useGetRelatedFields,
} from "@/api/related-fields";
import { generateTable } from "@/config/dashboard/general";
import { writingsColumns, writingsColumnsVisibility } from "@/config/dashboard/writings";
import { Writing } from "@/lib/types";
import { ColumnDef, VisibilityState } from "@tanstack/react-table";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { RelatedRecordRowActions } from "@/components/table/RelatedRecordRowActions";
import { createActionsColumnDef } from "@/components/table/table-actions-column";
import ShowElements from "../drawer/ShowElements";
import { LinkableType } from "../LinksManagement";
import { DataTable } from "../ui/datatable";
import { Input } from "../ui/input";
import { RelatedLinksListGridLayout } from "./RelatedLinksListGridLayout";
import { GridCardWithActions } from "./GridCardWithActions";
import { WritingLinkGridCard } from "./relatedLinkGridCards";

interface LinksProps {
  id: string;
  type: LinkableType;
}

/**
 * ArtworkLinks component
 * @returns {JSX.Element}
 */
const WritingLinks = ({ id, type }: LinksProps) => {
  const { t } = useTranslation();

  const [getPaginationRelatedWritings, setPaginationRelatedWritings] =
    useState<PagingParameters>({
      page: 0,
      pageSize: 10,
    });

  const { data: relatedFields } = useGetRelatedFields(
    id,
    type,
    "relatedWriting",
    getPaginationRelatedWritings.pageSize,
    getPaginationRelatedWritings.page * getPaginationRelatedWritings.pageSize,
  );

  const { data: countRelatedWritings } = useCountRelatedFields(id, type, [
    "relatedWriting",
  ]);

  const { mutate: createRelatedFields } = useCreateRelatedFields(
    id,
    type,
    "relatedWriting",
  );
  const { mutate: deleteRelation } = useDeleteRelation(
    id,
    type,
    "relatedWriting",
  );

  const [searchContent, setSearchContent] = useState("");

  const handleAddMode = () => {
    setIsAddMode(!isAddMode);
  };

  const [isAddMode, setIsAddMode] = useState(false);
  const [selectedWritingId, setSelectedWritingId] = useState<string | null>(null);

  const [getPagination, setPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });

  const { data: notRelatedWritings } = useGetNotRelatedResource<Writing>({
    resultType: "writing",
    resourceId: id,
    pagination: getPagination,
    searchQuery: searchContent,
  });

  const handleCreateRelation = useCallback(
    (idToLink: string) => {
      createRelatedFields([id, idToLink]);
    },
    [createRelatedFields, id],
  );

  const handleDeleteRelation = useCallback(
    (writingId: string) => {
      deleteRelation(writingId);
    },
    [deleteRelation],
  );

  const baseColumns = useMemo(
    () => generateTable(writingsColumns, "writing", t),
    [t],
  );

  const renderWritingRowActions = useCallback(
    (writing: Writing) => (
      <RelatedRecordRowActions
        isAddMode={isAddMode}
        resourceId={writing.id}
        previewType="writing"
        previewElements={[
          {
            label: t("ecritsDescriptionNumeroEnregistrement"),
            value: writing.ecritsDescriptionNumeroEnregistrement,
            type: "text",
          },
          {
            label: t("ecritsDescriptionLegendeRedigee"),
            value: writing.ecritsDescriptionLegendeRedigee,
            type: "richtext",
          },
          {
            label: t("attachments"),
            value: writing.attachments,
            type: "image",
          },
        ]}
        onDelete={() => handleDeleteRelation(writing.id)}
        onAdd={() => handleCreateRelation(writing.id)}
      />
    ),
    [handleCreateRelation, handleDeleteRelation, isAddMode, t],
  );

  const columns: ColumnDef<Writing, any>[] = useMemo(() => {
    return [
      ...baseColumns,
      createActionsColumnDef<Writing>(({ row }) =>
        renderWritingRowActions(row.original),
      ),
    ];
  }, [baseColumns, renderWritingRowActions]);

  const STORAGE_KEY = "writing-links-table";
  const defaultVisibility = { ...writingsColumnsVisibility, actions: true };
  const [columnsOrder, setColumnsOrder] = useState<string[]>(() => {
    try {
      const saved = readTablePreferences(STORAGE_KEY);
      if (saved) {
        const { order } = saved;
        return order || [];
      }
    } catch {
      // ignore
    }
    return [];
  });
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    () => {
      try {
        const saved = readTablePreferences(STORAGE_KEY);
        if (saved) {
          const { visibility } = saved;
          if (visibility && typeof visibility === "object")
            return { ...defaultVisibility, ...visibility };
        }
      } catch {
        // ignore
      }
      return { ...defaultVisibility };
    },
  );

  const tableOptions = {
    enableColumnResizing: true,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnOrderChange: setColumnsOrder,
    state: { columnVisibility, columnOrder: columnsOrder },
  };

  const relatedPaginationProps = {
    pagination: getPaginationRelatedWritings,
    setPage: setPaginationRelatedWritings,
    totalSize: countRelatedWritings?.relatedWriting ?? 0,
  };

  return (
    <RelatedLinksListGridLayout
      storageKey={STORAGE_KEY}
      title={t("linkedWritings")}
      isAddMode={isAddMode}
      onBack={handleAddMode}
      onToggleAdd={handleAddMode}
      searchInput={
        <div className="flex items-center gap-2">
          <Input
            placeholder={t("search")}
            className="w-80"
            value={searchContent}
            onChange={(e) => setSearchContent(e.target.value)}
          />
        </div>
      }
      addModeTable={
        <DataTable
          columns={columns}
          data={notRelatedWritings?.data ?? []}
          pagination={{
            pagination: getPagination,
            setPage: setPagination,
            totalSize: notRelatedWritings?.totalSize ?? 0,
          }}
          storageKey={STORAGE_KEY}
          options={tableOptions}
        />
      }
      listTable={
        <DataTable
          columns={columns}
          data={relatedFields ?? []}
          pagination={relatedPaginationProps}
          storageKey={STORAGE_KEY}
          options={tableOptions}
        />
      }
      gridContent={
        <>
          {relatedFields?.map((row: Writing) => (
            <div key={row.id}>
              <GridCardWithActions actions={renderWritingRowActions(row)}>
                <WritingLinkGridCard
                  item={row}
                  t={t}
                  onTextClick={() => setSelectedWritingId(row.id)}
                />
              </GridCardWithActions>
              <ShowElements
                type="writing"
                id={row.id}
                open={selectedWritingId === row.id}
                onOpenChange={(open) => {
                  if (!open) {
                    setSelectedWritingId(null);
                  }
                }}
                trigger={<span />}
                elements={[
                  {
                    label: t("ecritsDescriptionNumeroEnregistrement"),
                    value: row.ecritsDescriptionNumeroEnregistrement,
                    type: "text",
                  },
                  {
                    label: t("ecritsDescriptionLegendeRedigee"),
                    value: row.ecritsDescriptionLegendeRedigee ?? "",
                    type: "richtext",
                  },
                  {
                    label: t("attachments"),
                    value: row.attachments,
                    type: "image",
                  },
                ]}
              />
            </div>
          ))}
        </>
      }
      relatedPagination={relatedPaginationProps}
    />
  );
};

export default WritingLinks;
